import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import { Formik, Form } from 'formik';
import { Dialog, Grid, Box, Typography, Divider, TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Loader from 'components/Loader';
import Input from 'components/Input';
import Select from 'components/Select';
import CEP from 'components/CEP';
import Button from 'components/Button';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCreditCardAlt, faCheckCircle, faUser, faPhone, faMapMarkerAlt, faBriefcase } from '@fortawesome/free-solid-svg-icons';

import { loadBusinessInfo } from 'pages/BusinessInfo/store/thunk';
import { loadClientAreaPlans, loadClientArea, updateActivateFirstCharge } from 'pages/ListClients/store/thunk';

import { formatDocument } from 'helpers/formatters';

import { validations } from './validations';

const getPaymentMethodOptions = title => {
  if(title?.includes('boleto')) {
    return [
      { label: 'Boleto', value: '0' },
      { label: 'Cartão de crédito', value: '3', disabled: true }
    ];
  }

  if(title?.includes('cartão de crédito')) {
    return [
      { label: 'Boleto', value: '0', disabled: true },
      { label: 'Cartão de crédito', value: '3' }
    ];
  }

  return [
    { label: 'Boleto', value: '0' },
    { label: 'Cartão de crédito', value: '3' }
  ];
}

const buildInstallmentsOptions = maxInstallments => {
  return Array.from({ length: maxInstallments || 0 }, (_, index) => ({
    label: `${index + 1}x`,
    value: `${index + 1}`
  }));
}

export default function ListClientsFirstCharge({ open, onClose, subscription, hasCreditCard }) {
  const dispatch = useDispatch();

  const { userId } = useSelector(state => state.profile);
  const { isLoading, isPlansLoading, plans } = useSelector(state => state.listClients);

  const {
    establishmentId,
    submit: {
      documentId,
      establishmentName,
      postalCode,
      street,
      number,
      district,
      city,
      state
    },
    isLoading: isEstablishmentLoading
  } = useSelector(state => state.businessInfo);

  const [isConfirmingData, setIsConfirmingData] = useState(false);
  const [confirmationValues, setConfirmationValues] = useState(null);
  const [isFirstChargeSuccess, setIsFirstChargeSuccess] = useState(false);
  const [isNoCreditCardModalOpen, setIsNoCreditCardModalOpen] = useState(false);

  const planOptions = plans?.map(plan => ({
    title: plan?.title,
    installments: plan?.installments,
    value: plan?.id,
    label: `${plan?.description} (${plan?.title})`
  }));

  useEffect(() => {
    dispatch(loadBusinessInfo({ userId, establishmentId }));
    dispatch(loadClientAreaPlans(userId, establishmentId));
  }, []);

  useEffect(() => {
    if(open) {
      setIsConfirmingData(false);
      setConfirmationValues(null);
      setIsNoCreditCardModalOpen(false);
    }
  }, [open]);

  const handlePaymentMethodChange = (event, formikProps) => {
    const { value } = event.target;

    if(value == '3' && +hasCreditCard == 0) {
      setIsNoCreditCardModalOpen(true);
    }

    formikProps.handleChange(event);
  }

  const initialValues = {
    documentId: formatDocument(confirmationValues?.documentId || documentId || subscription?.documentId || ''),
    companyName: confirmationValues?.companyName || establishmentName ||  '',
    fantasyName: confirmationValues?.fantasyName || subscription?.fantasyName || '',
    phone: confirmationValues?.phone || '',
    email: confirmationValues?.email || (subscription?.email?.includes(';') ? subscription?.email?.split(';')[0] : subscription?.email) || '',
    postalCode: confirmationValues?.postalCode || postalCode || '',
    address: confirmationValues?.address || street || '',
    addressNumber: confirmationValues?.addressNumber || number || '',
    district: confirmationValues?.district || district || '',
    city: confirmationValues?.city || city || '',
    state: confirmationValues?.state || state || '',
    planId: confirmationValues?.planId || '',
    paymentMethodId: confirmationValues?.paymentMethodId || '',
    installments: confirmationValues?.installments || ''
  }

  const handleAdvanceToConfirmation = values => {
    setConfirmationValues(values);
    setIsConfirmingData(true);
  }

  const handleBackToEditing = () => {
    setIsConfirmingData(false);
  }

  const handleConfirmSubmit = () => {
    dispatch(updateActivateFirstCharge(userId, establishmentId, confirmationValues)).then(() => {
      toast.success('Cobrança gerada com sucesso!');
      dispatch(loadClientArea(userId, establishmentId));
      setIsFirstChargeSuccess(true);
      onClose();
    });
  }

  const selectedPlanLabel = planOptions?.find(option => option?.value == confirmationValues?.planId)?.label;
  const selectedPlan = planOptions?.find(option => option?.value == confirmationValues?.planId);
  const selectedPaymentMethodLabel = getPaymentMethodOptions(selectedPlan?.title || '')?.find(option => option?.value == confirmationValues?.paymentMethodId)?.label;

  return(
    <>
      {isNoCreditCardModalOpen && (
        <Dialog
          open={isNoCreditCardModalOpen}
          onClose={() => setIsNoCreditCardModalOpen(false)}
          fullWidth
          maxWidth='sm'
          scroll='paper'
        >
          <div style={{ padding: 20, width: '100%', boxSizing: 'border-box' }}>
            <div style={{ width: 'fit-content', backgroundColor: '#F5F5F5', border: '1px solid #D7D7D7', borderRadius: 8, padding: 10 }}>
              <FontAwesomeIcon
                size="lg"
                color="#000000"
                icon={faCreditCardAlt}
              />
            </div>
            <Typography variant="h6" style={{ marginTop: 20 }}>
              <b>Cartão não cadastrado</b>
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 20, marginBottom: 20 }}>
              Este cliente ainda não possui um cartão de crédito cadastrado. Para continuar com esta forma de pagamento, solicite ao cliente que cadastre o cartão através do link abaixo.
            </Typography>
            <div style={{ backgroundColor: '#F5F5F5', border: '1px solid #D7D7D7', borderRadius: 8, padding: 10 }}>
              <Typography>
                <b style={{ wordBreak: 'break-all' }}>
                  {window.location.origin}/cadastro-cartao
                </b>
              </Typography>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
              <Button
                type="button"
                color="tertiary"
                variant="outlined"
                onClick={() => setIsNoCreditCardModalOpen(false)}
              >
                Fechar
              </Button>
              <Button
                type="button"
                color="primary"
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/cadastro-cartao`);
                  toast.success('Link copiado com sucesso');
                }}
              >
                Copiar link de cadastro
              </Button>
            </div>
          </div>
        </Dialog>
      )}
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth='md'
        scroll='paper'
      >
        <div style={{ padding: 20, width: '100%', boxSizing: 'border-box' }}>
          <Loader
            isLoading={isLoading || isPlansLoading || isEstablishmentLoading}
            style={{ width: '100%' }}
          >
            <Grid container style={{ marginBottom: 20 }}>
              <Grid item xs={12}>
                <Typography variant="h6">
                  <b>{isConfirmingData ? 'Confirmar dados' : 'Primeiro pagamento'}</b>
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                  {isConfirmingData ? 'Revise as informações antes de gerar a cobrança.' : 'Preencha os dados abaixo para gerar a cobrança do primeiro pagamento.'}
                </Typography>
                <Divider />
              </Grid>
            </Grid>
            {isConfirmingData ? (
              <>
                {isFirstChargeSuccess && (
                  <div style={{ backgroundColor: '#f3fdf5', border: '1px solid #437a50', borderRadius: 8, padding: 16, marginBottom: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <FontAwesomeIcon icon={faCheckCircle} size="lg" color="#437a50" />
                      <Typography variant="subtitle1" style={{ color: '#437a50' }}>
                        <b>Cobrança gerada com sucesso!</b>
                      </Typography>
                    </div>
                  </div>
                )}
                <div style={{ border: '1px solid #E0E0E0', borderRadius: 8, padding: 16, marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 6, backgroundColor: '#F5F5F5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <FontAwesomeIcon icon={faUser} size="sm" />
                    </div>
                    <Typography variant="subtitle1">
                      <b>Dados cadastrais</b>
                    </Typography>
                  </div>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="caption" color="textSecondary" style={{ letterSpacing: 0.5 }}>
                        CNPJ OU CPF
                      </Typography>
                      <Typography variant="body1">
                        <b>{confirmationValues?.documentId || '--'}</b>
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="caption" color="textSecondary" style={{ letterSpacing: 0.5 }}>
                        NOME OU RAZÃO SOCIAL
                      </Typography>
                      <Typography variant="body1">
                        <b>{confirmationValues?.companyName || '--'}</b>
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="caption" color="textSecondary" style={{ letterSpacing: 0.5 }}>
                        NOME FANTASIA
                      </Typography>
                      <Typography variant="body1">
                        <b>{confirmationValues?.fantasyName || '--'}</b>
                      </Typography>
                    </Grid>
                  </Grid>
                </div>
                <div style={{ border: '1px solid #E0E0E0', borderRadius: 8, padding: 16, marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 6, backgroundColor: '#F5F5F5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <FontAwesomeIcon icon={faPhone} size="sm" />
                    </div>
                    <Typography variant="subtitle1">
                      <b>Contato</b>
                    </Typography>
                  </div>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="caption" color="textSecondary" style={{ letterSpacing: 0.5 }}>
                        TELEFONE
                      </Typography>
                      <Typography variant="body1">
                        <b>{confirmationValues?.phone || '--'}</b>
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="caption" color="textSecondary" style={{ letterSpacing: 0.5 }}>
                        E-MAIL PRINCIPAL
                      </Typography>
                      <Typography variant="body1">
                        <b>{confirmationValues?.email || '--'}</b>
                      </Typography>
                    </Grid>
                  </Grid>
                </div>
                <div style={{ border: '1px solid #E0E0E0', borderRadius: 8, padding: 16, marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 6, backgroundColor: '#F5F5F5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <FontAwesomeIcon icon={faMapMarkerAlt} size="sm" />
                    </div>
                    <Typography variant="subtitle1">
                      <b>Endereço</b>
                    </Typography>
                  </div>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="caption" color="textSecondary" style={{ letterSpacing: 0.5 }}>
                        CEP
                      </Typography>
                      <Typography variant="body1">
                        <b>{confirmationValues?.postalCode || '--'}</b>
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="caption" color="textSecondary" style={{ letterSpacing: 0.5 }}>
                        ENDEREÇO
                      </Typography>
                      <Typography variant="body1">
                        <b>{confirmationValues?.address || '--'}</b>
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="caption" color="textSecondary" style={{ letterSpacing: 0.5 }}>
                        NÚMERO
                      </Typography>
                      <Typography variant="body1">
                        <b>{confirmationValues?.addressNumber || '--'}</b>
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="caption" color="textSecondary" style={{ letterSpacing: 0.5 }}>
                        BAIRRO
                      </Typography>
                      <Typography variant="body1">
                        <b>{confirmationValues?.district || '--'}</b>
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="caption" color="textSecondary" style={{ letterSpacing: 0.5 }}>
                        CIDADE
                      </Typography>
                      <Typography variant="body1">
                        <b>{confirmationValues?.city || '--'}</b>
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="caption" color="textSecondary" style={{ letterSpacing: 0.5 }}>
                        ESTADO
                      </Typography>
                      <Typography variant="body1">
                        <b>{confirmationValues?.state || '--'}</b>
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="caption" color="textSecondary" style={{ letterSpacing: 0.5 }}>
                        PAÍS
                      </Typography>
                      <Typography variant="body1">
                        <b>Brasil</b>
                      </Typography>
                    </Grid>
                  </Grid>
                </div>
                <div style={{ border: '1px solid #E0E0E0', borderRadius: 8, padding: 16, marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 6, backgroundColor: '#F5F5F5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <FontAwesomeIcon icon={faBriefcase} size="sm" />
                    </div>
                    <Typography variant="subtitle1">
                      <b>Informações comerciais</b>
                    </Typography>
                  </div>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="caption" color="textSecondary" style={{ letterSpacing: 0.5 }}>
                        ID ESTABELECIMENTO
                      </Typography>
                      <Typography variant="body1">
                        <b>{establishmentId || '--'}</b>
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="caption" color="textSecondary" style={{ letterSpacing: 0.5 }}>
                        BUSINESS ID
                      </Typography>
                      <Typography variant="body1">
                        <b>{subscription?.businessId || '--'}</b>
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="caption" color="textSecondary" style={{ letterSpacing: 0.5 }}>
                        PLANO SELECIONADO
                      </Typography>
                      <Typography variant="body1">
                        <b>{selectedPlanLabel || '--'}</b>
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="caption" color="textSecondary" style={{ letterSpacing: 0.5 }}>
                        FORMA DE PAGAMENTO
                      </Typography>
                      <Typography variant="body1">
                        <b>{selectedPaymentMethodLabel || '--'}</b>
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="caption" color="textSecondary" style={{ letterSpacing: 0.5 }}>
                        PARCELAS
                      </Typography>
                      <Typography variant="body1">
                        <b>{confirmationValues?.installments || '--'}</b>
                      </Typography>
                    </Grid>
                  </Grid>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
                  <Button
                    type="button"
                    variant="outlined"
                    color="tertiary"
                    onClick={handleBackToEditing}
                  >
                    Voltar e editar
                  </Button>
                  <Button
                    type="button"
                    color="primary"
                    onClick={handleConfirmSubmit}
                  >
                    Confirmar e gerar cobrança
                  </Button>
                </div>
              </>
            ) : (
              <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validations}
                validateOnChange={false}
                onSubmit={handleAdvanceToConfirmation}
              >
                {({ ...formikProps }) => {
                  const selectedPlan = planOptions?.find(option => option?.value == formikProps.values.planId);
                  const installmentsOptions = buildInstallmentsOptions(selectedPlan?.installments);

                  return(
                    <Form>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                          <Box component='div' style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 10 }}>
                            <Typography variant='body2'>
                              <b>CNPJ ou CPF</b>
                            </Typography>
                            <Typography color='error'>*</Typography>
                          </Box>
                          <Input
                            type="document"
                            name="documentId"
                            label="CNPJ ou CPF"
                            value={formikProps.values.documentId}
                            error={!!formikProps.errors.documentId}
                            helperText={formikProps.errors.documentId}
                            onChange={formikProps.handleChange}
                            onBlur={formikProps.handleBlur}
                          />
                        </Grid>
                        <Grid item xs={12} md={8}>
                          <Box component='div' style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 10 }}>
                            <Typography variant='body2'>
                              <b>Nome ou razão social</b>
                            </Typography>
                            <Typography color='error'>*</Typography>
                          </Box>
                          <Input
                            type="text"
                            name="companyName"
                            label="Nome ou razão social"
                            value={formikProps.values.companyName}
                            error={!!formikProps.errors.companyName}
                            helperText={formikProps.errors.companyName}
                            onChange={formikProps.handleChange}
                          />
                        </Grid>
                      </Grid>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Box component='div' style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 10 }}>
                            <Typography variant='body2'>
                              <b>Nome Fantasia</b>
                            </Typography>
                            <Typography color='error'>*</Typography>
                          </Box>
                          <Input
                            type="text"
                            name="fantasyName"
                            label="Nome Fantasia"
                            value={formikProps.values.fantasyName}
                            error={!!formikProps.errors.fantasyName}
                            helperText={formikProps.errors.fantasyName}
                            onChange={formikProps.handleChange}
                          />
                        </Grid>
                      </Grid>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                          <Box component='div' style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 10 }}>
                            <Typography variant='body2'>
                              <b>Telefone</b>
                            </Typography>
                            <Typography color='error'>*</Typography>
                          </Box>
                          <Input
                            type="phone"
                            name="phone"
                            label="Telefone"
                            value={formikProps.values.phone}
                            error={!!formikProps.errors.phone}
                            helperText={formikProps.errors.phone}
                            onChange={formikProps.handleChange}
                          />
                        </Grid>
                        <Grid item xs={12} md={8}>
                          <Box component='div' style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 10 }}>
                            <Typography variant='body2'>
                              <b>E-mail principal</b>
                            </Typography>
                            <Typography color='error'>*</Typography>
                          </Box>
                          <Input
                            type="email"
                            name="email"
                            label="E-mail principal"
                            value={formikProps.values.email}
                            error={!!formikProps.errors.email}
                            helperText={formikProps.errors.email}
                            onChange={formikProps.handleChange}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Box component='div' style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 10 }}>
                            <Typography variant='body2'>
                              <b>CEP</b>
                            </Typography>
                            <Typography color='error'>*</Typography>
                          </Box>
                          <CEP
                            name="postalCode"
                            label="CEP"
                            value={formikProps.values.postalCode}
                            error={!!formikProps.errors.postalCode}
                            helperText={formikProps.errors.postalCode}
                            onChange={postalCode => {
                              if(!postalCode.erro) {
                                const { cep, logradouro, bairro, localidade, uf } = postalCode;

                                formikProps.setFieldValue('postalCode', cep);
                                formikProps.setFieldValue('address', logradouro);
                                formikProps.setFieldValue('district', bairro);
                                formikProps.setFieldValue('city', localidade);
                                formikProps.setFieldValue('state', uf);
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={8}>
                          <Box component='div' style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 10 }}>
                            <Typography variant='body2'>
                              <b>Endereço</b>
                            </Typography>
                            <Typography color='error'>*</Typography>
                          </Box>
                          <Input
                            type="text"
                            name="address"
                            label="Endereço"
                            value={formikProps.values.address}
                            error={!!formikProps.errors.address}
                            helperText={formikProps.errors.address}
                            onChange={formikProps.handleChange}
                          />
                        </Grid>
                      </Grid>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                          <Box component='div' style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 10 }}>
                            <Typography variant='body2'>
                              <b>Número</b>
                            </Typography>
                            <Typography color='error'>*</Typography>
                          </Box>
                          <Input
                            type="number"
                            name="addressNumber"
                            label="Número"
                            value={formikProps.values.addressNumber}
                            error={!!formikProps.errors.addressNumber}
                            helperText={formikProps.errors.addressNumber}
                            onChange={formikProps.handleChange}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Box component='div' style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 10 }}>
                            <Typography variant='body2'>
                              <b>Bairro</b>
                            </Typography>
                            <Typography color='error'>*</Typography>
                          </Box>
                          <Input
                            type="text"
                            name="district"
                            label="Bairro"
                            value={formikProps.values.district}
                            error={!!formikProps.errors.district}
                            helperText={formikProps.errors.district}
                            onChange={formikProps.handleChange}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Box component='div' style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 10 }}>
                            <Typography variant='body2'>
                              <b>Cidade</b>
                            </Typography>
                            <Typography color='error'>*</Typography>
                          </Box>
                          <Input
                            type="text"
                            name="city"
                            label="Cidade"
                            value={formikProps.values.city}
                            error={!!formikProps.errors.city}
                            helperText={formikProps.errors.city}
                            onChange={formikProps.handleChange}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Box component='div' style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 10 }}>
                            <Typography variant='body2'>
                              <b>Estado</b>
                            </Typography>
                            <Typography color='error'>*</Typography>
                          </Box>
                          <Input
                            type="text"
                            name="state"
                            label="Estado"
                            value={formikProps.values.state}
                            error={!!formikProps.errors.state}
                            helperText={formikProps.errors.state}
                            onChange={formikProps.handleChange}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Typography variant='body2'>
                            <b>País</b>
                          </Typography>
                          <Input
                            disabled
                            type="text"
                            name="country"
                            label="País"
                            value="Brasil"
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Box component='div' style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 10 }}>
                            <Typography variant='body2'>
                              <b>ID estabelecimento</b>
                            </Typography>
                            <Typography color='error'>*</Typography>
                          </Box>
                          <Input
                            disabled
                            type="number"
                            name="establishmentId"
                            label="ID estabelecimento"
                            value={establishmentId}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Box component='div' style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 10 }}>
                            <Typography variant='body2'>
                              <b>Business ID</b>
                            </Typography>
                            <Typography color='error'>*</Typography>
                          </Box>
                          <Input
                            disabled
                            type="number"
                            name="businessId"
                            label="Business ID"
                            value={subscription?.businessId}
                            helperText="ID do estabelecimento — 5 dígitos"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Box component='div' style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 10 }}>
                            <Typography variant='body2'>
                              <b>Seleção de plano</b>
                            </Typography>
                            <Typography color='error'>*</Typography>
                          </Box>
                          <Autocomplete
                            id="planId"
                            fullWidth
                            openOnFocus
                            disabled={isPlansLoading}
                            options={planOptions || []}
                            value={planOptions?.find(option => option?.value == formikProps.values.planId) || null}
                            onChange={(_, params) => {
                              formikProps.setFieldValue('planId', params?.value || '');
                              formikProps.setFieldValue('paymentMethodId', '');
                              formikProps.setFieldValue('installments', '');
                            }}
                            getOptionSelected={(option, value) => option?.value == value?.value}
                            getOptionLabel={option => option?.label || ''}
                            renderInput={params => (
                              <TextField
                                {...params}
                                variant="outlined"
                                color="primary"
                                label="Seleção de plano"
                                error={!!formikProps.errors.planId}
                                helperText={formikProps.errors.planId}
                              />
                            )}
                          />
                        </Grid>
                        {!!formikProps.values.planId && (
                          <>
                            <Grid item md={6} xs={12}>
                              <Box component='div' style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 10 }}>
                                <Typography variant='body2'>
                                  <b>Forma de pagamento</b>
                                </Typography>
                                <Typography color='error'>*</Typography>
                              </Box>
                              <Select
                                name="paymentMethodId"
                                label="Forma de pagamento"
                                value={formikProps.values.paymentMethodId}
                                options={getPaymentMethodOptions(planOptions?.find(plan => +plan?.value == +formikProps.values.planId)?.title || '')}
                                error={!!formikProps.errors.paymentMethodId}
                                helperText={formikProps.errors.paymentMethodId}
                                onChange={event => handlePaymentMethodChange(event, formikProps)}
                              />
                              {(+hasCreditCard == 0) && (
                                <Typography variant="body2" color="error" style={{ marginTop: 8 }}>
                                  Cliente sem cartão cadastrado.{' '}
                                  <Typography
                                    component="span"
                                    color="primary"
                                    variant="body2"
                                    style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                    onClick={() => setIsNoCreditCardModalOpen(true)}
                                  >
                                    Ver instruções
                                  </Typography>
                                  .
                                </Typography>
                              )}
                            </Grid>
                            <Grid item md={6} xs={12}>
                              <Box component='div' style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 10 }}>
                                <Typography variant='body2'>
                                  <b>Parcelas</b>
                                </Typography>
                                <Typography color='error'>*</Typography>
                              </Box>
                              <Select
                                name="installments"
                                label="Parcelas"
                                value={formikProps.values.installments}
                                options={installmentsOptions}
                                error={!!formikProps.errors.installments}
                                helperText={formikProps.errors.installments}
                                onChange={formikProps.handleChange}
                              />
                            </Grid>
                          </>
                        )}
                      </Grid>
                      <Grid container style={{ marginTop: 20 }}>
                        <Grid item xs={12}>
                          <Divider />
                        </Grid>
                      </Grid>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
                        <Button
                          type="button"
                          variant="outlined"
                          color="tertiary"
                          onClick={onClose}
                        >
                          Cancelar
                        </Button>
                        <Button
                          disabled={+formikProps.values.paymentMethodId == 3 && +hasCreditCard == 0}
                          type="submit"
                          color="primary"
                        >
                          Gerar cobrança
                        </Button>
                      </div>
                    </Form>
                  );
                }}
              </Formik>
            )}
          </Loader>
        </div>
      </Dialog>
    </>
  );
}